import { ReactNode, useCallback, useEffect, useState } from "react";

import { ToolbarLayerContext } from "./ToolbarLayerContext";

interface ToolbarLayerProviderProps {
    children: ReactNode;
    /** Column-level hover state — drives tooltip visibility for standard toolbars. */
    areaHovered?: boolean;
    /** Bypasses areaHovered + flyout computation entirely. Used by fly-out toolbars to pass their own isHovered. */
    tooltipsVisibleOverride?: boolean;
}

export function ToolbarLayerProvider({
    children,
    areaHovered = false,
    tooltipsVisibleOverride,
}: ToolbarLayerProviderProps) {
    const [flyoutOpenCount, setFlyoutOpenCount] = useState(0);
    // Stays true after a flyout closes until the user genuinely re-enters the
    // column (areaHovered false → true). Prevents tooltips flashing when a
    // flyout closes while the mouse is still inside the column DOM (e.g. the
    // user clicked a button inside the flyout itself).
    const [suppressUntilRehover, setSuppressUntilRehover] = useState(false);

    const onFlyoutOpenChange = useCallback((open: boolean) => {
        setFlyoutOpenCount((prev) => (open ? prev + 1 : Math.max(0, prev - 1)));
        if (!open) {
            setSuppressUntilRehover(true);
        }
    }, []);

    useEffect(() => {
        if (areaHovered) {
            setSuppressUntilRehover(false);
        }
    }, [areaHovered]);

    const tooltipsVisible = tooltipsVisibleOverride ?? (areaHovered && flyoutOpenCount === 0 && !suppressUntilRehover);

    return (
        <ToolbarLayerContext.Provider value={{ tooltipsVisible, onFlyoutOpenChange }}>
            {children}
        </ToolbarLayerContext.Provider>
    );
}
